import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';

import type { CadsFormControlConfig } from './cads-form-control-config';

/**
 * Shared override shape: callers can supply any subset of the descriptor (most often `key`, `errorMessages`, or
 * deeper `typeOptions` overrides).
 */
type CadsFormControlOverrides<K extends CadsFormControlConfig['kind']> = Partial<CadsFormControlConfig<K>>;

/**
 * Preset phone validator: 10–15 **digits** after stripping separators; only `+ ( )` digits, spaces, and `-` allowed.
 * Returns `{ pattern: true }` so {@link createPhoneControl} can keep a single `pattern` error message. Empty values
 * return `null` so {@link Validators.required} owns empties.
 */
function cadsPhonePresetValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value == null || value === '') {
    return null;
  }
  if (typeof value !== 'string') {
    return { pattern: true };
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (!/^[+()\d\s-]+$/.test(trimmed)) {
    return { pattern: true };
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) {
    return { pattern: true };
  }
  return null;
}

/**
 * First-name input preset: required, minimum length 2, autocomplete `given-name`. Each call returns a fresh
 * `FormControl` so descriptors are safe to use in multiple forms simultaneously.
 */
export function createFirstNameControl(
  overrides: CadsFormControlOverrides<'input'> = {},
): CadsFormControlConfig<'input'> {
  return {
    key: 'firstName',
    kind: 'input',
    control: new FormControl<string | null>('', [Validators.required, Validators.minLength(2)]),
    typeOptions: {
      type: 'text',
      label: 'First name',
      autocomplete: 'given-name',
    },
    errorMessages: {
      required: 'Enter your first name',
      minlength: 'First name must be at least 2 characters',
    },
    ...overrides,
  };
}

/** Last-name input preset: required, minimum length 2, autocomplete `family-name`. */
export function createLastNameControl(
  overrides: CadsFormControlOverrides<'input'> = {},
): CadsFormControlConfig<'input'> {
  return {
    key: 'lastName',
    kind: 'input',
    control: new FormControl<string | null>('', [Validators.required, Validators.minLength(2)]),
    typeOptions: {
      type: 'text',
      label: 'Last name',
      autocomplete: 'family-name',
    },
    errorMessages: {
      required: 'Enter your last name',
      minlength: 'Last name must be at least 2 characters',
    },
    ...overrides,
  };
}

/** Email input preset: required + `Validators.email`, autocomplete `email`, `inputMode: 'email'`. */
export function createEmailControl(
  overrides: CadsFormControlOverrides<'input'> = {},
): CadsFormControlConfig<'input'> {
  return {
    key: 'email',
    kind: 'input',
    control: new FormControl<string | null>('', [Validators.required, Validators.email]),
    typeOptions: {
      type: 'email',
      label: 'Email',
      placeholder: 'name@example.com',
      autocomplete: 'email',
      inputMode: 'email',
    },
    errorMessages: {
      required: 'Enter your email',
      email: 'Enter a valid email',
    },
    ...overrides,
  };
}

/**
 * Phone input preset: required + 10–15 **digits** after stripping separators; allows `+ ( )` digits, spaces, and
 * hyphens. Uses `tel` / `inputMode: 'tel'` with **no** display mask so longer international values stay editable. For
 * strict NANP `(###) ###-####` formatting, merge `mask: 'us-phone'` into `typeOptions` (10-digit display only—pair with
 * a stricter validator if needed); shallow overrides replace the whole `typeOptions` object.
 */
export function createPhoneControl(
  overrides: CadsFormControlOverrides<'input'> = {},
): CadsFormControlConfig<'input'> {
  return {
    key: 'phone',
    kind: 'input',
    control: new FormControl<string | null>('', [Validators.required, cadsPhonePresetValidator]),
    typeOptions: {
      type: 'tel',
      label: 'Phone number',
      placeholder: '(555) 123-4567',
      autocomplete: 'tel',
      inputMode: 'tel',
    },
    errorMessages: {
      required: 'Enter your phone number',
      pattern: 'Enter a valid phone number',
    },
    ...overrides,
  };
}

/** US 5-digit zip input preset: required + 5-digit pattern, `inputMode: 'numeric'`. */
export function createZipControl(
  overrides: CadsFormControlOverrides<'input'> = {},
): CadsFormControlConfig<'input'> {
  return {
    key: 'zip',
    kind: 'input',
    control: new FormControl<string | null>('', [
      Validators.required,
      Validators.pattern(/^\d{5}$/),
    ]),
    typeOptions: {
      type: 'text',
      label: 'Zip code',
      placeholder: '12345',
      autocomplete: 'postal-code',
      inputMode: 'numeric',
      maxLength: 5,
      minLength: 5,
    },
    errorMessages: {
      required: 'Enter your zip code',
      pattern: 'Enter a valid 5-digit zip code',
    },
    ...overrides,
  };
}
